import React, { useEffect, useState } from "react";
import Spinner from "react-spinkit";
import styled from "styled-components";
import { useGetConfigs } from "api/controller-configs";
import PencilButton from "components/ActionButtons/PencilButton";
import Table from "components/Table/Table";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import { TruncatedText, ActionCell, Icon } from "../../components/Table/tableComponents";
import CardHeader from "./CardHeader";
import ConfigModal from "./ConfigModal";

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: flex-center;
  margin: 15px;
  padding: 20px;
`;

const fallbackCopyText = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  textarea.style.left = "-1000px";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const copyToClipboard = async (value, snackbarContext) => {
  const text = typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      fallbackCopyText(text);
    }

    snackbarContext.setNotificationTimeOut(
      Color.success,
      "Valor copiado."
    );
  } catch (err) {
    snackbarContext.setNotificationTimeOut(
      Color.danger,
      "Falha ao copiar o valor."
    );
  }
};

const headers = [
  {
    label: "Nome", key: "name",
  },
  {
    label: "Código", key: "code",
  },
  {
    label: "Descrição",
    key: "description",
    valueFormatter: (value) => (
      <TruncatedText title={value} width="300px">
        {value}
      </TruncatedText>
    ),
  },
  {
    label: "Valor atual",
    key: "value",
    valueFormatter: (value) => {
      const text = (
        typeof value === "object"
          ? JSON.stringify(value)
          : String(value)
      );

      return (
        <TruncatedText title={text} width="350px">
          {text}
        </TruncatedText>
      );
    },
  },
  {
    label: "", key: "copy", className: "table-action",
  },
  {
    label: "", key: "edit", className: "table-action"
  },
];

function parseData(value) {
  try {
    return JSON.stringify(value);
  } catch (e) {
    return value;
  }
};


function ConfigsTable({
  processName,
  processCode,
  snackbarContext,
}) {
  const { configs, configsLoading, configsRefetch } = useGetConfigs(processCode);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const onSelectConfig = config => () => {
    setModalOpen(true);
    setSelectedConfig(config);
  };

  const mapDataToTable = cfgs => cfgs.map(config => ({
    name: config.name,
    code: config.code,
    description: config.description,
    value: parseData(config.value),
    copy: (
      <ActionCell>
        <Icon className="far fa-copy" onClick={() => copyToClipboard(config.value, snackbarContext)} />
      </ActionCell>
    ),
    edit: (
      <PencilButton onPencilEditClick={onSelectConfig(config)} />
    ),
  }));

  const [tableItems, setTableItems] = useState(mapDataToTable(configs));

  useEffect(() => {
    configsRefetch();
  }, [modalOpen, configsRefetch]);

  useEffect(() => {
    setTableItems(mapDataToTable(configs));
  }, [configs]);

  return (
    <React.Fragment>
      <CardHeader title={`Configurações do Processo: ${processName}`} />
      {
        configsLoading && (
          <SpinnerContainer>
            <Spinner name="line-spin-fade-loader" color="green" />
          </SpinnerContainer>
        )
      }
      { !configsLoading && tableItems.length === 0 && <p>Nenhuma configuração encontrada.</p> }
      { !configsLoading && tableItems.length > 0 && (
        <React.Fragment>
          <Table tableName="processes" headers={headers} matchHeaders items={tableItems} maxHeight="1200px" />
          {Boolean(selectedConfig) && (
            <ConfigModal
              title={`Configuração: ${selectedConfig ? selectedConfig.name : ""}`}
              isOpen={modalOpen}
              setIsOpen={setModalOpen}
              config={selectedConfig}
              setSelectedConfig={setSelectedConfig}
            />
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default withSnackbarConsumer(ConfigsTable);
